// src/translations.js
const translations = {
    en: {
      title: "Free Toyota ERC Headunit Unlock Tool",
      explanation: "Our tool provides a quick and easy way to generate ERC unlock codes for free, helping you restore full functionality to your Japanese Toyota head unit without the need for costly dealership visits.",
      navigationButtons: {
        whatIsErc: "What is ERC?",
        howToFind: "How do I find my 16 digit code?"
      },
      inputLabel: "ERC Code (16 characters):",
      inputPlaceholder: "e.g. 9F8E7D6C5B4A3210",
      decodeButton: "Decode",
      generateAnotherButton: "Generate Another Code",
      errorMessage: "Invalid number of characters.",
      whatIsErc: {
        title: "What is ERC?",
        description: "The Emergency Rescue Code (ERC) is a unique, vehicle-specific code used to unlock the head unit of certain Japanese Toyota vehicles. This code is necessary when the head unit becomes locked, which can occur due to security features activated after events like a battery disconnection or incorrect entry attempts. Without the correct ERC, the head unit remains inoperable, preventing access to important functions such as navigation, audio, and other in-car systems. ERCs ensure that only authorized users can regain control of the head unit, thus enhancing the security of the vehicle's infotainment system."
      },
      howToFind: {
        title: "How do I find my 16 digit code?",
        youtubeNote: "The best way to find the exact steps for your model is to search your model number on YouTube. Every unit can be slightly different. Note: some models require you to turn your headlights on and off as part of the sequence.",
        description: "There are two common methods for accessing the ERC code screen. Try Method 1 first. If it does not work on your unit, try Method 2.",
        steps: [
          "Press and Hold the Main Button: On your navigation player, press and hold the main button.",
          "Turn Parking Lights On/Off: Switch your parking lights on and off 3 to 4 times until a new screen appears on the head unit.",
          "Press the Active Buttons: Follow the prompts on the screen and press only the active buttons. This will lead you to a screen displaying the 16-digit ERC serial number.",
          "If these steps do not work for your specific model, you may need to research the procedure for your particular vehicle or consult additional resources."
        ],
        methods: [
          {
            title: "Method 1: MAP Button + Small Lamp",
            intro: "Enter service inspection mode by pressing and holding the Current Location (MAP) button on the navigation unit while turning the small lamp on and off 3 to 4 times.",
            steps: [
              "Press and hold the Current Location (MAP) button on the navigation unit.",
              "While holding the button, turn the vehicle's small lamp ON and OFF 3 to 4 times.",
              "Release the button when 'Service Inspection Mode' appears on the screen.",
              "Select 'Service Information' from the displayed menu.",
              "Touch the 'Security Code Entry' button.",
              "The 16-digit ERC code will be displayed on the screen. Note this code down."
            ]
          },
          {
            title: "Method 2: Picture Quality Adjustment Mode",
            intro: "Access service inspection mode by performing specific touch operations from the picture quality adjustment screen.",
            steps: [
              "Long press the MENU button on the navigation unit.",
              "When the picture quality adjustment mode appears, touch the following positions on the screen in sequence: Upper left (A), Lower left (B), Upper left (A), Lower left (B), Upper left (A), Lower left (B).",
              "When touched correctly, the screen will switch to service inspection mode.",
              "Select 'Service Information' from the displayed menu.",
              "Touch the 'Security Code Entry' button.",
              "The 16-digit ERC code will be displayed on the screen. Note this code down."
            ]
          }
        ]
      },
      supportedModels: {
        title: "Supported Models",
        description: "Our ERC unlock tool supports a wide range of Toyota head units, including the following models:"
      },
      credits: {
        freeUnlocksRemaining: "Free unlocks remaining: {{count}}",
        creditsAvailable: "Credits available: {{count}}",
        buyCredits: "Buy Credits",
        needCredits: "You need credits to continue unlocking"
      },
      payment: {
        title: "Choose Your Unlock Package",
        subtitle: "First 2 unlocks are FREE! Save more with bulk packages.",
        credits: "credits",
        perUnlock: "per unlock",
        save: "Save",
        popularBadge: "POPULAR",
        purchaseButton: "Purchase Now",
        processing: "Processing...",
        securePayment: "🔒 Secure payment powered by Stripe",
        errorMessage: "Payment failed. Please try again."
      },
      whyLocked: {
        title: "Why Does My Toyota Head Unit Get Locked?",
        intro: "If you own a Japanese-import Toyota vehicle, you may have encountered the frustrating situation of your head unit suddenly locking up and displaying a code entry screen. This is not a malfunction, it is a deliberate anti-theft security feature built into many Japanese domestic market (JDM) Toyota vehicles.",
        sections: [
          {
            heading: "The Anti-Theft System",
            text: "Toyota designed the ERC lock system specifically for vehicles sold in the Japanese domestic market. When the head unit detects a potential theft situation, such as a sudden loss of power from the battery being disconnected, it automatically locks itself. This prevents thieves from removing the unit and using it in another vehicle, since it becomes useless without the correct unlock code."
          },
          {
            heading: "Common Triggers",
            text: "The head unit will lock itself in several common situations: disconnecting or replacing the car battery, a flat battery that fully drains, electrical work on the vehicle, or multiple incorrect code entry attempts. Many owners of imported Japanese vehicles encounter this when performing routine maintenance such as a battery replacement, not realising the head unit will lock as a result."
          },
          {
            heading: "Why Imported Vehicles Are Affected",
            text: "Japanese domestic market vehicles were originally sold in Japan with the expectation that owners would visit a Toyota dealership to retrieve the unlock code. However, when these vehicles are exported and imported to other countries like New Zealand, Australia, the UK, or Russia, the original dealership support is no longer available. This is exactly why tools like ours exist, to give imported vehicle owners a free, fast, and reliable way to unlock their head unit without an expensive dealership visit or shipping the car back to Japan."
          }
        ]
      },
      importGuide: {
        title: "Guide for Japanese Import Toyota Owners",
        intro: "Japanese domestic market Toyota vehicles are popular imports in many countries, valued for their reliability, low mileage, and well-maintained condition. However, owning a JDM Toyota comes with some unique considerations that differ from locally-sold vehicles.",
        sections: [
          {
            heading: "Head Unit Differences",
            text: "JDM Toyota head units are often different from those sold in other markets. They may display menus in Japanese, have navigation systems loaded with Japanese maps, and include features like digital TV tuners designed for Japanese broadcast standards. Many owners replace or update these units, but if you want to keep the original, understanding how to manage the ERC lock system is essential."
          },
          {
            heading: "Battery Maintenance Tips",
            text: "One of the most common reasons head units lock on imported vehicles is routine battery replacement. Before disconnecting your battery for any reason, including tyre changes, servicing, or battery replacement, note down your ERC code or bookmark this tool. That way, if your head unit locks after the battery is reconnected, you can unlock it within seconds."
          },
          {
            heading: "Keeping Your ERC Code Safe",
            text: "Once you have successfully retrieved your 16-digit ERC serial number from your head unit, store it somewhere safe. Keep a note in your car's service book, save it in your phone, or store it in a secure notes app. You may need it again in the future, and having it ready saves time. Our tool will always be here as a backup if you lose it."
          },
          {
            heading: "When to Seek Professional Help",
            text: "While our ERC unlock tool handles the vast majority of Japanese Toyota head units, some older or very new models may use a different locking system. If you enter the generated code and it does not work after several attempts, it is worth consulting a specialist auto electrician or an importer who deals specifically with JDM vehicles. Avoid entering incorrect codes repeatedly, as some units will permanently lock after too many failed attempts."
          }
        ]
      },
      faq: {
        title: "Frequently Asked Questions",
        items: [
          {
            question: "Is this tool completely free to use?",
            answer: "Yes. Every new user gets 2 free unlocks with no sign-up or payment required. If you need more unlocks, for example if you are a mechanic or car dealer who regularly works with imported Toyota vehicles, affordable credit packs are available."
          },
          {
            question: "Why is my Toyota head unit locked?",
            answer: "Your head unit locks automatically as an anti-theft measure. This most commonly happens after the car battery is disconnected or replaced, after a flat battery fully drains, or after multiple incorrect code entry attempts. It is a security feature built into Japanese domestic market Toyota vehicles."
          },
          {
            question: "Will this tool work on my specific model?",
            answer: "Our tool supports a wide range of Japanese Toyota head units. Check the supported models list on this page to see if your unit is included. If your model is not listed, it may use a different ERC calculation method, and you may need to seek specialist help."
          },
          {
            question: "Is it legal to unlock my own head unit?",
            answer: "Yes, absolutely. You are unlocking your own vehicle's head unit. This tool is intended for vehicle owners and automotive professionals who need to restore access to a head unit that has been locked due to normal circumstances such as a battery change. It is not intended for use on stolen vehicles."
          },
          {
            question: "What if the code does not work?",
            answer: "First, double-check that you have entered your 16-digit ERC serial number correctly, a single wrong character will produce an incorrect unlock code. Make sure you retrieved the serial from the head unit itself and not from a sticker or manual. If the code still does not work, your head unit may use a different algorithm not covered by this tool."
          },
          {
            question: "Do I need any technical knowledge to use this?",
            answer: "No technical knowledge is required. Simply retrieve the 16-digit code displayed on your locked head unit, enter it into the tool on this page, and your unlock code is generated instantly. The process of entering the code into your head unit is straightforward, see the how-to guide above."
          },
          {
            question: "How long does the unlock process take?",
            answer: "Generating the unlock code takes less than a second once you enter your 16-digit serial number. Entering the code into your head unit and completing the unlock process typically takes 1 to 2 minutes."
          },
          {
            question: "Can I use this tool for multiple vehicles?",
            answer: "Yes. Each vehicle has a unique 16-digit ERC serial number, so the generated unlock code is different for every vehicle. If you work with multiple imported Toyota vehicles, our credit packs are designed with mechanics and dealers in mind and offer significant savings per unlock."
          }
        ]
      },
      troubleshooting: {
        title: "Troubleshooting Common Issues",
        intro: "If you are having trouble unlocking your Toyota head unit, the following guide covers the most common problems and how to solve them.",
        items: [
          {
            problem: "I cannot get the head unit to display the 16-digit code",
            solution: "The exact method to retrieve the ERC serial number varies by model and year. The most common method is to press and hold the main power button while toggling the parking lights on and off. Some models require holding a different button combination. Try searching for your specific model number (usually printed on a sticker on the unit itself) along with 'ERC code retrieval' for model-specific instructions."
          },
          {
            problem: "The unlock code I generated does not work",
            solution: "The most common cause is a typo in the 16-digit serial number. ERC codes use both numbers and letters (A-F), so make sure you have not confused the letter O with the number 0, or the letter I with the number 1. Re-enter your serial number carefully and generate the code again. If it still fails, your unit may use a variant algorithm not covered by this tool."
          },
          {
            problem: "My head unit locked again after I already unlocked it",
            solution: "The head unit re-locks whenever it detects a power interruption. This is normal behaviour. If your battery is old and repeatedly going flat, it may be triggering the lock regularly. Consider replacing your battery. In the meantime, our tool is always available to regenerate the code whenever you need it."
          },
          {
            problem: "The head unit screen is blank or completely unresponsive",
            solution: "If the screen is blank rather than showing a code entry prompt, this may indicate a different issue, a blown fuse, a loose wiring connector, or a faulty screen rather than an ERC lock. Check your vehicle's fuse box for any blown fuses related to the audio or navigation system. If fuses are fine, the unit may need professional inspection."
          },
          {
            problem: "I have used all my free unlocks",
            solution: "Our free tier provides 2 unlocks for new users. If you need additional unlocks, you can purchase credit packs at an affordable rate. These are ideal for mechanics, automotive dealers, and anyone who regularly works with imported Japanese Toyota vehicles."
          }
        ]
      },
      nav: {
        home: "Home",
        whatIsErc: "What is ERC?",
        howToFind: "How to Find Your Code",
        supportedModels: "Supported Models",
        faq: "FAQ",
        troubleshooting: "Troubleshooting"
      },
      contact: {
        title: "Contact Us",
        subtitle: "Have a question about the tool or your unlock code? We're here to help.",
        nameLabel: "Your Name",
        namePlaceholder: "e.g. John Smith",
        emailLabel: "Email Address",
        emailPlaceholder: "e.g. john@example.com",
        subjectLabel: "Subject",
        subjectPlaceholder: "e.g. My unlock code isn't working",
        messageLabel: "Message",
        messagePlaceholder: "Tell us what you need help with...",
        submitButton: "Send Message",
        submitting: "Sending...",
        successTitle: "Message sent!",
        successMessage: "Thanks for getting in touch. We'll get back to you as soon as possible.",
        errorMessage: "Something went wrong. Please try again."
      },
      steps: {
        step1: {
          title: "Your head unit is locked",
          description: "Your head unit will display a screen like this when locked. You need to navigate to the ERC serial screen to find your 16-digit code."
        },
        step2: {
          title: "Find your stereo model number",
          description: "The model number is printed on a sticker on the head unit itself. Once you have it, search it on YouTube to find the exact button sequence needed to reach the ERC serial screen on your specific model.",
          note: "💡 Note: some models require you to turn your headlights on and off as part of the sequence."
        },
        step3: {
          title: "Enter the 16-digit code into the tool above",
          description: "Type the 16-digit code shown on this screen into the tool at the top of this page. You will receive your unlock code instantly. Enter that unlock code into your stereo to restore full functionality."
        }
      }
    },
    ja: {
      title: "無料 Toyota ERC ヘッドユニット ロック解除ツール",
      explanation: "高価なディーラー訪問なしに、ToyotaヘッドユニットのERCロック解除コードを無料で素早く生成できるツールです。バッテリー交換後などにヘッドユニットがロックされた場合にご利用ください。",
      navigationButtons: {
        whatIsErc: "ERCとは?",
        howToFind: "16桁のコードをどうやって見つけるの?"
      },
      inputLabel: "ERCコード (16文字):",
      inputPlaceholder: "例: 9F8E7D6C5B4A3210",
      decodeButton: "デコード",
      generateAnotherButton: "別のコードを生成",
      errorMessage: "文字数が無効です。",
      whatIsErc: {
        title: "ERCとは何ですか？",
        description: "緊急救助コード（ERC）は、Toyota車両のヘッドユニットをロック解除するための、車両固有のコードです。このコードは、バッテリー切断や不正なエントリー試行などのイベント後に有効化されるセキュリティ機能により、ヘッドユニットがロックされた場合に必要となります。正しいERCがないと、ヘッドユニットは操作不能となり、ナビゲーション、オーディオ、その他の車内システムへのアクセスができなくなります。ERCは、許可されたユーザーのみがヘッドユニットの制御を取り戻せるようにする盗難防止機能です。"
      },
      howToFind: {
        title: "16桁のコードを見つける方法は？",
        youtubeNote: "お使いのモデルに合った正確な手順を確認するには、型番をYouTubeで検索するのが最も確実です。機種によって操作手順が異なります。また、一部のモデルではヘッドライトを点灯・消灯する操作が手順に含まれる場合があります。",
        description: "ERCコード画面にアクセスするための方法は主に2つあります。まず方法1をお試しください。うまくいかない場合は方法2をお試しください。",
        steps: [
          "メインボタンを長押しする：ナビゲーションプレーヤーで、メインボタンを長押しします。",
          "駐車灯のオン/オフ：駐車灯を3〜4回オンとオフに切り替えて、ヘッドユニットに新しい画面を表示させます。",
          "アクティブボタンを押す：画面のプロンプトに従い、アクティブなボタンのみを押します。これにより、16桁のERCシリアル番号が表示される画面が表示されます。",
          "これらの手順が特定のモデルで機能しない場合は、お使いの特定の車両の手順を調べるか、追加のリソースを参照する必要があります。"
        ],
        methods: [
          {
            title: "方法1：現在地（MAP）ボタン＋スモールランプ",
            intro: "ナビゲーションユニットの現在地（MAP）ボタンを押しながら、スモールランプを3〜4回オン/オフにすることでサービス点検モードに入ります。",
            steps: [
              "ナビゲーションユニットの現在地（MAP）ボタンを押し続けます。",
              "ボタンを押したまま、車両のスモールランプを3〜4回オン/オフに切り替えます。",
              "画面に「サービス点検モード」が表示されたらボタンを離します。",
              "表示されたメニューから「サービス情報」を選択します。",
              "「セキュリティコード入力」ボタンをタッチします。",
              "16桁のERCコードが画面に表示されます。メモしてください。"
            ]
          },
          {
            title: "方法2：画質調整モードから操作",
            intro: "画質調整画面から特定のタッチ操作を行うことで、サービス点検モードにアクセスします。",
            steps: [
              "ナビゲーションユニットのMENUボタンを長押しします。",
              "画質調整モードが表示されたら、画面上の以下の位置を順にタッチします：左上（A）、左下（B）、左上（A）、左下（B）、左上（A）、左下（B）。",
              "正しくタッチできると、画面がサービス点検モードに切り替わります。",
              "表示されたメニューから「サービス情報」を選択します。",
              "「セキュリティコード入力」ボタンをタッチします。",
              "16桁のERCコードが画面に表示されます。メモしてください。"
            ]
          }
        ]
      },
      supportedModels: {
        title: "サポートされているモデル",
        description: "私たちのERCロック解除ツールは、以下のモデルを含む幅広いToyotaヘッドユニットをサポートしています："
      },
      credits: {
        freeUnlocksRemaining: "残りの無料ロック解除: {{count}}",
        creditsAvailable: "利用可能なクレジット: {{count}}",
        buyCredits: "クレジットを購入",
        needCredits: "ロック解除を続けるにはクレジットが必要です"
      },
      payment: {
        title: "ロック解除パッケージを選択",
        subtitle: "最初の2回のロック解除は無料！まとめ買いでさらにお得。",
        credits: "クレジット",
        perUnlock: "ロック解除あたり",
        save: "節約",
        popularBadge: "人気",
        purchaseButton: "今すぐ購入",
        processing: "処理中...",
        securePayment: "🔒 Stripeによる安全な支払い",
        errorMessage: "支払いに失敗しました。もう一度お試しください。"
      },
      whyLocked: {
        title: "Toyotaヘッドユニットがロックされるのはなぜですか？",
        intro: "Toyotaを所有していると、ヘッドユニットが突然ロックされ、コード入力画面が表示されることがあります。これは故障ではなく、Toyotaのヘッドユニットに組み込まれた盗難防止セキュリティ機能です。",
        sections: [
          {
            heading: "盗難防止システム",
            text: "ToyotaのERCロックシステムは、ヘッドユニットの盗難を防ぐために設計されています。ヘッドユニットがバッテリーの切断など不審な状況を検知すると、自動的にロックがかかります。これにより、盗まれたユニットを別の車両で使用することができなくなります。正しいロック解除コードがなければ使用不可になるため、盗難の抑止力となっています。"
          },
          {
            heading: "ロックされる主な原因",
            text: "ヘッドユニットがロックされる主な原因は、バッテリーの交換や取り外し、バッテリーの完全放電、車両への電装工事、複数回の誤ったコード入力などです。バッテリー交換のような日常的なメンテナンスの後にロックがかかることも多く、知らずに驚かれる方も少なくありません。"
          },
          {
            heading: "ディーラーに頼らず自分で解決する",
            text: "通常、ERCロック解除はトヨタディーラーに依頼することになりますが、費用がかかる場合があります。当ツールを使えば、ディーラーに行かずに無料でロック解除コードを取得できます。16桁のERCシリアル番号をヘッドユニットから取得し、このページのツールに入力するだけで、すぐにロック解除コードが発行されます。"
          }
        ]
      },
      importGuide: {
        title: "Toyotaオーナーのための実用ガイド",
        intro: "Toyotaのヘッドユニットは高品質で信頼性が高いですが、ERCロックシステムについて知っておくと、いざという時に慌てずに対処できます。",
        sections: [
          {
            heading: "ヘッドユニットの型番を確認する",
            text: "ヘッドユニットの型番は、本体に貼られたステッカーに記載されています。型番が分かれば、YouTubeや各種サイトでERCシリアル番号画面への操作手順を調べることができます。型番によって操作手順が異なるため、まず自分のモデルを確認することが重要です。"
          },
          {
            heading: "バッテリー交換前にやっておくこと",
            text: "バッテリー交換などでバッテリーを取り外す前に、このツールをブックマークしておくか、ERCコードをメモしておくと安心です。バッテリーを再接続した後にヘッドユニットがロックされても、数秒でロックを解除できます。"
          },
          {
            heading: "ERCコードを手元に保管する",
            text: "ヘッドユニットから16桁のERCシリアル番号を取得したら、スマートフォンのメモアプリや車の取扱説明書に記録しておくとよいでしょう。将来また必要になる場合があるので、安全な場所に保管しておくことをお勧めします。"
          },
          {
            heading: "専門家に相談すべき場合",
            text: "当ツールは多くのToyotaヘッドユニットに対応していますが、一部の古いモデルや特定のモデルでは異なるシステムが使用されている場合があります。生成されたコードが機能しない場合は、カーオーディオ専門店や自動車電装士に相談されることをお勧めします。"
          }
        ]
      },
      faq: {
        title: "よくある質問",
        items: [
          {
            question: "このツールは完全に無料ですか？",
            answer: "はい。すべての新規ユーザーは、サインアップや支払いなしで2回の無料ロック解除を受け取ります。複数のToyota車を扱うメカニックや自動車ディーラーなど、さらに多くのロック解除が必要な場合は、手頃な価格のクレジットパックをご利用いただけます。"
          },
          {
            question: "なぜToyotaのヘッドユニットがロックされるのですか？",
            answer: "ヘッドユニットは盗難防止措置として自動的にロックされます。これは、バッテリーの切断または交換後、バッテリーが完全に放電した後、または複数回の誤ったコード入力後に最もよく起こります。ToyotaのERCロックシステムは車両のセキュリティを高めるために設計された機能です。"
          },
          {
            question: "私の特定のモデルに対応していますか？",
            answer: "当ツールは幅広いToyotaヘッドユニットをサポートしています。このページのサポートモデルリストでお使いのユニットが含まれているかご確認ください。モデルが記載されていない場合は、異なるERC計算方法を使用している可能性があります。"
          },
          {
            question: "自分のヘッドユニットをロック解除することは合法ですか？",
            answer: "はい、全く問題ありません。ご自身の車両のヘッドユニットをロック解除しています。このツールは、バッテリー交換などの通常の状況でロックされたヘッドユニットへのアクセスを回復する必要がある車両のオーナーおよび自動車専門家を対象としています。"
          },
          {
            question: "コードが機能しない場合はどうすればよいですか？",
            answer: "まず、16桁のERCシリアル番号を正しく入力したか再確認してください。1文字でも間違えると誤ったロック解除コードが生成されます。文字のOと数字の0、文字のIと数字の1を混同していないか注意してください。"
          },
          {
            question: "技術的な知識は必要ですか？",
            answer: "技術的な知識は必要ありません。ロックされたヘッドユニットに表示されている16桁のコードを取得し、このページのツールに入力するだけで、ロック解除コードが即座に生成されます。"
          },
          {
            question: "ロック解除プロセスにはどのくらいの時間がかかりますか？",
            answer: "16桁のシリアル番号を入力してからロック解除コードの生成は1秒未満で完了します。ヘッドユニットにコードを入力してロック解除プロセスを完了するには通常1〜2分かかります。"
          },
          {
            question: "複数の車両にこのツールを使用できますか？",
            answer: "はい。各車両には固有の16桁のERCシリアル番号があるため、生成されるロック解除コードは車両ごとに異なります。複数のToyota車を扱うメカニックやディーラーには、クレジットパックがお得です。1回あたりのロック解除コストを大幅に節約できます。"
          }
        ]
      },
      troubleshooting: {
        title: "一般的な問題のトラブルシューティング",
        intro: "Toyotaヘッドユニットのロック解除に問題が発生している場合、以下のガイドで最も一般的な問題とその解決方法を説明します。",
        items: [
          {
            problem: "ヘッドユニットに16桁のコードを表示させることができない",
            solution: "ERCシリアル番号を取得する正確な方法は、モデルや年式によって異なります。最も一般的な方法は、メインの電源ボタンを長押ししながら駐車灯をオン・オフに切り替える方法です。モデル番号（通常はユニット自体のステッカーに記載）とともに「ERCコード取得」を検索して、モデル固有の手順を確認してください。"
          },
          {
            problem: "生成されたロック解除コードが機能しない",
            solution: "最も一般的な原因は、16桁のシリアル番号の入力ミスです。ERCコードには数字と文字（A-F）の両方が使用されるため、文字OとOの数字、文字IとIの数字を混同していないか確認してください。シリアル番号を注意深く再入力し、コードを再生成してください。"
          },
          {
            problem: "ロック解除後にヘッドユニットが再びロックされた",
            solution: "ヘッドユニットは電源が遮断されるたびに再ロックされます。これは正常な動作です。バッテリーが古く、定期的に放電している場合は、定期的にロックが発生する可能性があります。バッテリーの交換を検討してください。"
          },
          {
            problem: "ヘッドユニットの画面が真っ白または全く反応しない",
            solution: "画面がコード入力プロンプトではなく空白の場合、これはERCロックではなく別の問題（ヒューズ切れ、配線コネクタの緩み、画面の故障など）を示している可能性があります。オーディオまたはナビゲーションシステムに関連するヒューズボックスを確認してください。"
          },
          {
            problem: "無料ロック解除をすべて使い切った",
            solution: "無料ティアは新規ユーザーに2回のロック解除を提供します。追加のロック解除が必要な場合は、手頃な価格でクレジットパックをご購入いただけます。これらは、輸入日本製Toyota車を定期的に扱うメカニックや自動車ディーラーに最適です。"
          }
        ]
      },
      nav: {
        home: "ホーム",
        whatIsErc: "ERCとは?",
        howToFind: "コードの見つけ方",
        supportedModels: "対応モデル",
        faq: "よくある質問",
        troubleshooting: "トラブルシューティング"
      },
      contact: {
        title: "お問い合わせ",
        subtitle: "ツールやロック解除コードについてご質問がありましたら、お気軽にお問い合わせください。",
        nameLabel: "お名前",
        namePlaceholder: "例：山田 太郎",
        emailLabel: "メールアドレス",
        emailPlaceholder: "例：taro@example.com",
        subjectLabel: "件名",
        subjectPlaceholder: "例：ロック解除コードが機能しない",
        messageLabel: "メッセージ",
        messagePlaceholder: "お困りの内容をお聞かせください...",
        submitButton: "送信する",
        submitting: "送信中...",
        successTitle: "送信完了！",
        successMessage: "お問い合わせありがとうございます。できるだけ早くご返信いたします。",
        errorMessage: "エラーが発生しました。もう一度お試しください。"
      },
      steps: {
        step1: {
          title: "ヘッドユニットがロックされています",
          description: "バッテリー交換などの後、ヘッドユニットがこのような画面を表示することがあります。ERCシリアル番号の画面に移動して、16桁のコードを取得する必要があります。"
        },
        step2: {
          title: "ステレオの型番を確認する",
          description: "型番はヘッドユニット本体のステッカーに記載されています。型番が分かったら、YouTubeで検索して、お使いのモデルのERCシリアル番号画面への操作手順を確認してください。",
          note: "💡 注意：一部のモデルでは、操作手順の一部としてヘッドライトのオン/オフが必要な場合があります。"
        },
        step3: {
          title: "16桁のコードをツールに入力する",
          description: "この画面に表示されている16桁のコードを、このページ上部のツールに入力してください。ロック解除コードが即座に生成されます。そのロック解除コードをステレオに入力すると、ヘッドユニットが使用可能になります。"
        }
      }
    },
    mn: {
      title: "Үнэгүй Toyota ERC Толгой төхөөрөмжийн түгжээ тайлах хэрэгсэл",
      explanation: "Манай хэрэгсэл нь Японы Toyota толгой төхөөрөмжийн түгжээг үнэ төлбөргүй, хурдан бөгөөд хялбар аргаар тайлах боломжийг олгодог бөгөөд үнэтэй дилерийн үйлчилгээний төлөө зочлохын оронд бүрэн үйл ажиллагааг сэргээхэд тусалдаг.",
      navigationButtons: {
        whatIsErc: "ERC гэж юу вэ?",
        howToFind: "16 оронтой кодыг хэрхэн олох вэ?"
      },
      inputLabel: "ERC код (16 тэмдэгт):",
      inputPlaceholder: "жишээ: 9F8E7D6C5B4A3210",
      decodeButton: "Тайлах",
      generateAnotherButton: "Өөр код үүсгэх",
      errorMessage: "Тэмдэгтийн тоо буруу байна.",
      whatIsErc: {
        title: "ERC гэж юу вэ?",
        description: "Яаралтай аврах код (ERC) нь зарим Японы Toyota автомашины толгой төхөөрөмжийн түгжээг тайлахад ашиглагддаг автомашинд тусгайлан зориулагдсан давтагдашгүй код юм. Энэ код нь батарей салгах эсвэл буруу оролт хийх зэрэг үйл явдлын дараа идэвхжсэн аюулгүй байдлын функцээс болж толгой төхөөрөмж түгжигдсэн үед шаардлагатай болдог. Зөв ERC байхгүй бол толгой төхөөрөмж ажиллахгүй бөгөөд навигаци, аудио болон бусад машин доторх системүүдэд хандахыг хориглодог. ERC нь зөвхөн эрх бүхий хэрэглэгчид толгой төхөөрөмжийн хяналтыг эргүүлэн авах боломжийг олгож, автомашины мэдээллийн үзвэр системийн аюулгүй байдлыг сайжруулдаг."
      },
      howToFind: {
        title: "16 оронтой кодыг хэрхэн олох вэ?",
        youtubeNote: "Таны загварт яг тохирсон алхмуудыг олохын тулд загварын дугаараа YouTube-д хайх нь хамгийн найдвартай арга юм. Загвар бүр арай өөр байдаг. Зарим загварт дарааллын нэг хэсэг болгон толгойн гэрлийг асааж унтраах шаардлагатай байж болно.",
        description: "ERC кодын дэлгэцэд хандах 2 нийтлэг арга байдаг. Эхлээд 1-р аргыг туршина уу. Ажиллахгүй бол 2-р аргыг туршина уу.",
        steps: [
          "Үндсэн товчлуурыг дараад барина уу: Навигацийн тоглуулагч дээр үндсэн товчлуурыг дараад барина уу.",
          "Зогсоолын гэрлийг асааж унтраана уу: Толгой төхөөрөмж дээр шинэ дэлгэц гарах хүртэл зогсоолын гэрлийг 3-4 удаа асааж унтраана уу.",
          "Идэвхтэй товчлууруудыг дарна уу: Дэлгэц дээрх удирдамжийг дагаж, зөвхөн идэвхтэй товчлууруудыг дарна уу. Энэ нь 16 оронтой ERC серийн дугаарыг харуулах дэлгэцэд хүргэнэ.",
          "Хэрэв эдгээр алхмууд таны тодорхой загварт ажиллахгүй бол танай тодорхой автомашины журмыг судлах эсвэл нэмэлт эх сурвалжтай зөвлөлдөх шаардлагатай байж болно."
        ],
        methods: [
          {
            title: "1-р арга: MAP товч + Жижиг гэрэл",
            intro: "Навигацийн нэгжийн Одоогийн байршил (MAP) товчийг дараад барин, жижиг гэрлийг 3-4 удаа асааж унтраах замаар үйлчилгээний шалгалтын горимд орно.",
            steps: [
              "Навигацийн нэгжийн Одоогийн байршил (MAP) товчийг дараад барина уу.",
              "Товчийг барин байхдаа автомашины жижиг гэрлийг 3-4 удаа асааж унтраана уу.",
              "Дэлгэц дээр 'Үйлчилгээний шалгалтын горим' гарч ирэхэд товчийг суллана уу.",
              "Гарч ирсэн цэснээс 'Үйлчилгээний мэдээлэл'-ийг сонгоно уу.",
              "'Аюулгүйн код оруулах' товчийг дарна уу.",
              "16 оронтой ERC код дэлгэц дээр гарч ирнэ. Тэмдэглэж аваарай."
            ]
          },
          {
            title: "2-р арга: Зургийн чанарын тохируулгын горим",
            intro: "Зургийн чанарын тохируулгын дэлгэцээс тодорхой хүрэлтийн үйлдэл хийж үйлчилгээний шалгалтын горимд орно.",
            steps: [
              "Навигацийн нэгжийн MENU товчийг удаан дарна уу.",
              "Зургийн чанарын тохируулгын горим гарч ирэхэд дэлгэцийн дараах байршлуудыг дарааллан дарна уу: Зүүн дээд (A), Зүүн доод (B), Зүүн дээд (A), Зүүн доод (B), Зүүн дээд (A), Зүүн доод (B).",
              "Зөв дарвал дэлгэц үйлчилгээний шалгалтын горимд шилжинэ.",
              "Гарч ирсэн цэснээс 'Үйлчилгээний мэдээлэл'-ийг сонгоно уу.",
              "'Аюулгүйн код оруулах' товчийг дарна уу.",
              "16 оронтой ERC код дэлгэц дээр гарч ирнэ. Тэмдэглэж аваарай."
            ]
          }
        ]
      },
      supportedModels: {
        title: "Дэмжигдсэн загварууд",
        description: "Манай ERC түгжээ тайлах хэрэгсэл нь дараах загваруудыг багтаасан Toyota толгой төхөөрөмжийн өргөн хүрээг дэмждэг:"
      },
      credits: {
        freeUnlocksRemaining: "Үлдсэн үнэгүй түгжээ тайлалт: {{count}}",
        creditsAvailable: "Боломжтой кредит: {{count}}",
        buyCredits: "Кредит худалдаж авах",
        needCredits: "Түгжээг үргэлжлүүлэн тайлахын тулд танд кредит хэрэгтэй"
      },
      payment: {
        title: "Түгжээ тайлах багцаа сонгоно уу",
        subtitle: "Эхний 2 түгжээ тайлалт ҮНЭГҮЙ! Бөөнөөр авахад илүү хэмнэнэ.",
        credits: "кредит",
        perUnlock: "нэг түгжээ тайлахад",
        save: "Хэмнэх",
        popularBadge: "АЛДАРТАЙ",
        purchaseButton: "Одоо худалдаж авах",
        processing: "Боловсруулж байна...",
        securePayment: "🔒 Stripe-ээр хамгаалагдсан төлбөр",
        errorMessage: "Төлбөр амжилтгүй болсон. Дахин оролдоно уу."
      },
      whyLocked: {
        title: "Toyota-ийн толгой төхөөрөмж яагаад түгждэг вэ?",
        intro: "Японоос импортолсон Toyota автомашинтай бол толгой төхөөрөмж гэнэт түгжигдэж, код оруулах дэлгэц гарч ирэх таагүй нөхцөлтэй тулгарсан байж болно. Энэ нь доголдол биш, харин олон Японы дотоод зах зээлийн Toyota машинд суулгасан санаатай хулгайн эсрэг хамгаалалтын функц юм.",
        sections: [
          {
            heading: "Хулгайн эсрэг систем",
            text: "Toyota нь Японы дотоод зах зээлд зориулан зарагдсан тээврийн хэрэгслүүдэд зориулан ERC түгжээний системийг тусгайлан зохион бүтээсэн. Толгой төхөөрөмж нь батарей салгах зэрэг хулгайн нөхцөл байдлыг илрүүлэхэд автоматаар түгжигддэг. Зөв ил тайлах код байхгүй бол хулгайч нэгжийг авч өөр машинд ашиглах боломжгүй болдог."
          },
          {
            heading: "Түгжигдэх нийтлэг шалтгаанууд",
            text: "Толгой төхөөрөмж хэд хэдэн нийтлэг нөхцөл байдалд түгжигддэг: батарей салгах эсвэл солих, батарей бүрэн цэнэггүй болох, машинд цахилгааны ажил хийх, эсвэл буруу код оруулах оролдлого олон удаа хийх. Импортын машины олон эзэн батарей солих зэрэг ердийн засвар үйлчилгээ хийх үед үүнтэй тулгардаг."
          },
          {
            heading: "Импортын машинд нөлөөлдөг шалтгаан",
            text: "Японы дотоод зах зээлийн машинуудыг эзэд ил тайлах кодыг авахын тулд Toyota-ийн дилерт очино гэж үзэн Японд зарсан. Гэвч эдгээр машинуудыг Шинэ Зеланд, Австрали, Их Британи, Орос зэрэг улсуудад экспортолж импортлоход анхны дилерийн дэмжлэг байхгүй болдог. Яг энэ шалтгаанаар манай хэрэгсэл оршин тогтнодог."
          }
        ]
      },
      importGuide: {
        title: "Японы импортын Toyota-ийн эзэдэд зориулсан гарын авлага",
        intro: "Японы дотоод зах зээлийн Toyota машинууд найдвартай байдал, бага гүйлт, сайн засварлагдсан байдлаараа олон улсад алдартай импортын машин юм. Гэхдээ JDM Toyota-г эзэмшихэд дотоодын зах зээлийн машинаас ялгаатай өвөрмөц анхаарах зүйлс байдаг.",
        sections: [
          {
            heading: "Толгой төхөөрөмжийн ялгаанууд",
            text: "JDM Toyota-ийн толгой төхөөрөмжүүд нь ихэвчлэн бусад зах зээлд зарагддагаас ялгаатай. Тэд Японы хэл дээр цэс харуулж, Японы газрын зургаар навигаци хийх систем, Японы нэвтрүүлгийн стандартад зориулсан тоон телевизийн тюнер агуулдаг. Анхны нэгжийг хадгалахыг хүсвэл ERC түгжээний системийг хэрхэн удирдахыг ойлгох нь чухал."
          },
          {
            heading: "Батарейны засвар үйлчилгээний зөвлөмжүүд",
            text: "Импортын машинд толгой төхөөрөмж түгжигдэх хамгийн нийтлэг шалтгааны нэг нь ердийн батарей солих явдал юм. Ямар нэг шалтгаанаар батарейгаа салгахаасаа өмнө энэ хэрэгслийг хадгалж авах эсвэл ERC кодыг тэмдэглэж аваарай. Ингэснээр батарей дахин холбосны дараа толгой төхөөрөмж түгжигдсэн ч хэдхэн секундэд ил тайлж чадна."
          },
          {
            heading: "ERC кодоо аюулгүй хадгалах",
            text: "Толгой төхөөрөмжөөсөө 16 оронтой ERC серийн дугаарыг амжилттай гаргаж авсны дараа аюулгүй газарт хадгалаарай. Машины үйлчилгээний дэвтэрт тэмдэглэх, утсандаа хадгалах, эсвэл аюулгүй тэмдэглэлийн апп-д хадгалж болно. Ирээдүйд дахин хэрэг болж магадгүй."
          },
          {
            heading: "Мэргэжилтний тусламж хэрэгтэй үе",
            text: "Манай ERC ил тайлах хэрэгсэл нь ихэнх Японы Toyota толгой төхөөрөмжийг дэмжих боловч зарим хуучин эсвэл маш шинэ загваруудад өөр түгжээний систем ашигладаг байж болно. Хэд хэдэн удаа оролдсоны дараа ажиллахгүй байвал JDM машин мэргэшсэн автомашины цахилгаанчид эсвэл импортлогчтой зөвлөлдөхийг санал болгоно."
          }
        ]
      },
      faq: {
        title: "Түгээмэл асуултууд",
        items: [
          {
            question: "Энэ хэрэгсэл бүрэн үнэгүй мөн үү?",
            answer: "Тийм ээ. Бүх шинэ хэрэглэгч бүртгэл эсвэл төлбөргүйгээр 2 удаагийн үнэгүй ил тайлалт авдаг. Импортын Toyota машинтай байнга ажилладаг механик эсвэл машины дилер зэрэг илүү их ил тайлалт хэрэгтэй хүмүүст хямд үнэтэй кредит багцууд байна."
          },
          {
            question: "Toyota-ийн толгой төхөөрөмж яагаад түгждэг вэ?",
            answer: "Толгой төхөөрөмж хулгайн эсрэг арга хэмжээний хувьд автоматаар түгждэг. Энэ нь ихэвчлэн батарей салгах эсвэл солих, батарей бүрэн цэнэггүй болох, эсвэл хэд хэдэн удаа буруу код оруулсны дараа тохиолддог. Энэ нь Японы дотоод зах зээлийн Toyota машинд суулгасан хамгаалалтын функц юм."
          },
          {
            question: "Миний тодорхой загварт тохирох уу?",
            answer: "Манай хэрэгсэл нь Японы Toyota толгой төхөөрөмжийн өргөн хүрээг дэмждэг. Таны нэгж орсон эсэхийг шалгахын тулд энэ хуудаснаас дэмжигдсэн загваруудын жагсаалтыг үзнэ үү."
          },
          {
            question: "Өөрийн толгой төхөөрөмжийг ил тайлах нь хууль ёсны мөн үү?",
            answer: "Тийм ээ, бүрэн хууль ёсны. Та өөрийн машины толгой төхөөрөмжийг ил тайлж байна. Энэ хэрэгсэл нь батарей солих зэрэг ердийн нөхцөл байдлаас болж түгжигдсэн толгой төхөөрөмжид хандах хандалтыг сэргээх шаардлагатай машины эзэд болон мэргэжлийн автомашины ажилчдад зориулагдсан."
          },
          {
            question: "Код ажиллахгүй бол яах вэ?",
            answer: "Эхлээд 16 оронтой ERC серийн дугаарыг зөв оруулсан эсэхийг дахин шалгаарай. Нэг тэмдэгт буруу байвал буруу ил тайлах код үүснэ. O үсэг ба 0 тоо, I үсэг ба 1 тоог андуурч ороогүй эсэхийг анхаарна уу."
          },
          {
            question: "Техникийн мэдлэг шаардлагатай юу?",
            answer: "Техникийн мэдлэг шаардлагагүй. Түгжигдсэн толгой төхөөрөмжид харагдаж байгаа 16 оронтой кодыг авч, энэ хуудасны хэрэгсэлд оруулахад л ил тайлах код нэн даруй үүснэ."
          },
          {
            question: "Ил тайлах процесс хэр удаан үргэлжлэх вэ?",
            answer: "16 оронтой серийн дугаар оруулснаас хойш ил тайлах код үүсгэхэд нэг секундээс бага хугацаа шаардагдана. Толгой төхөөрөмжид код оруулж ил тайлах процессыг дуусгахад ихэвчлэн 1-2 минут шаардагдана."
          },
          {
            question: "Олон тооны машинд энэ хэрэгслийг ашиглаж болох уу?",
            answer: "Тийм ээ. Машин бүр өвөрмөц 16 оронтой ERC серийн дугаартай тул үүсгэгдэх ил тайлах код машин бүрт өөр байна. Олон импортын Toyota машинтай ажилладаг бол кредит багцууд механик болон дилерүүдэд зориулан боловсруулагдсан бөгөөд нэг ил тайлалт тутам мэдэгдэхүйц хэмнэлт санал болгодог."
          }
        ]
      },
      troubleshooting: {
        title: "Нийтлэг асуудлуудыг шийдвэрлэх",
        intro: "Toyota толгой төхөөрөмжийн түгжээг тайлахад бэрхшээл гарч байвал доорх гарын авлага нь хамгийн нийтлэг асуудал болон тэдгээрийг шийдвэрлэх арга замыг тайлбарлана.",
        items: [
          {
            problem: "Толгой төхөөрөмжид 16 оронтой кодыг харуулж чадахгүй байна",
            solution: "ERC серийн дугаарыг авах яг нарийн аргачлал нь загвар болон жилээс хамааран ялгаатай байдаг. Хамгийн нийтлэг арга бол зогсоолын гэрлийг асааж унтраах явцад үндсэн цахилгаан товчийг дарж барих явдал юм. Загварынхаа дугаарыг (ихэвчлэн нэгж дээрх наалт дээр бичигдсэн) ERC код авах хэсэгтэй хамт хайх."
          },
          {
            problem: "Үүсгэсэн ил тайлах код ажиллахгүй байна",
            solution: "Хамгийн нийтлэг шалтгаан нь 16 оронтой серийн дугаарт бичгийн алдаа гаргах явдал юм. ERC кодууд нь тоо ба үсэг (A-F) хоёуланг агуулдаг тул O үсэг ба 0 тоо, I үсэг ба 1 тоог андуурч ороогүй эсэхийг шалгана уу. Серийн дугаарыг анхааралтай дахин оруулж, кодыг дахин үүсгэнэ үү."
          },
          {
            problem: "Ил тайлсаны дараа толгой төхөөрөмж дахин түгжигдлээ",
            solution: "Толгой төхөөрөмж цахилгааны тасалдал илрүүлэх бүрт дахин түгждэг. Энэ нь хэвийн зан үйл юм. Батарей хуучирсан бол байнга цэнэггүй болж түгжигдэлт нь байнга гарч болно. Батарейгаа солихыг авч үзнэ үү."
          },
          {
            problem: "Толгой төхөөрөмжийн дэлгэц хоосон байна эсвэл хариу өгөхгүй байна",
            solution: "Дэлгэц нь код оруулах хэсгийн оронд хоосон харагдаж байвал энэ нь ERC түгжилт биш, өөр асуудал байж болзошгүй: хайлсан запобитель, сул утасны холбогч, эсвэл дэлгэцийн доголдол. Аудио эсвэл навигацийн системтэй холбоотой хайлсан запобительүүдийг шалгана уу."
          },
          {
            problem: "Үнэгүй ил тайлалтуудаа бүгдийг ашигласан",
            solution: "Үнэгүй хэсэг нь шинэ хэрэглэгчдэд 2 ил тайлалт олгодог. Нэмэлт ил тайлалт хэрэгтэй бол хямд үнэтэй кредит багцуудыг худалдаж авах боломжтой. Импортын Японы Toyota машинтай байнга ажилладаг механик болон автомашины дилерүүдэд тохиромжтой."
          }
        ]
      },
      nav: {
        home: "Нүүр",
        whatIsErc: "ERC гэж юу?",
        howToFind: "Код хэрхэн олох",
        supportedModels: "Дэмжигдсэн загварууд",
        faq: "Түгээмэл асуултууд",
        troubleshooting: "Алдаа засах"
      },
      contact: {
        title: "Холбоо барих",
        subtitle: "Хэрэгсэл эсвэл ил тайлах кодтой холбоотой асуулт байвал бидэнтэй холбоо барина уу.",
        nameLabel: "Таны нэр",
        namePlaceholder: "Жишээ: Бат Болд",
        emailLabel: "И-мэйл хаяг",
        emailPlaceholder: "Жишээ: bat@example.com",
        subjectLabel: "Сэдэв",
        subjectPlaceholder: "Жишээ: Ил тайлах код ажиллахгүй байна",
        messageLabel: "Мессеж",
        messagePlaceholder: "Ямар тусламж хэрэгтэйгээ бидэнд хэлнэ үү...",
        submitButton: "Мессеж илгээх",
        submitting: "Илгээж байна...",
        successTitle: "Мессеж илгээгдлээ!",
        successMessage: "Холбоо барьсанд баярлалаа. Аль болох хурдан хариулах болно.",
        errorMessage: "Алдаа гарлаа. Дахин оролдоно уу."
      },
      steps: {
        step1: {
          title: "Толгой төхөөрөмж түгжигдсэн байна",
          description: "Батарей солих зэрэг үйлдлийн дараа толгой төхөөрөмж ийм дэлгэц харуулж болно. ERC серийн дугаарын дэлгэц рүү шилжиж, 16 оронтой кодыг олох хэрэгтэй."
        },
        step2: {
          title: "Стерео төхөөрөмжийн загварын дугаарыг олох",
          description: "Загварын дугаар нь толгой төхөөрөмж дээрх наалт дээр байна. Загварын дугаараа мэдсэний дараа YouTube-д хайж, тухайн загварын ERC серийн дугаарын дэлгэц рүү хэрхэн орохыг олж мэдэх боломжтой.",
          note: "💡 Анхаарна уу: зарим загварын дараалалд толгойн гэрлийг асааж унтраах шаардлагатай байж болно."
        },
        step3: {
          title: "16 оронтой кодыг дээрх хэрэгсэлд оруулах",
          description: "Энэ дэлгэцэнд харагдаж байгаа 16 оронтой кодыг энэ хуудасны дээрх хэрэгсэлд оруулна уу. Ил тайлах код нэн даруй гарна. Тэр кодыг стереод оруулж толгой төхөөрөмжийг ил тайлна уу."
        }
      }
    },
    ur: {
      title: "مفت Toyota ERC ہیڈ یونٹ ان لاک ٹول",
      explanation: "ہمارا ٹول مفت میں ERC ان لاک کوڈز بنانے کا ایک تیز اور آسان طریقہ فراہم کرتا ہے، جو آپ کو مہنگے ڈیلرشپ کی ملاقات کی ضرورت کے بغیر اپنے جاپانی Toyota ہیڈ یونٹ کی مکمل فعالیت بحال کرنے میں مدد کرتا ہے۔",
      navigationButtons: {
        whatIsErc: "ERC کیا ہے؟",
        howToFind: "میں اپنا 16 ہندسوں کا کوڈ کیسے تلاش کروں؟"
      },
      inputLabel: "ERC کوڈ (16 حروف):",
      inputPlaceholder: "مثال: 9F8E7D6C5B4A3210",
      decodeButton: "ڈیکوڈ",
      generateAnotherButton: "دوسرا کوڈ بنائیں",
      errorMessage: "حروف کی تعداد غلط ہے۔",
      whatIsErc: {
        title: "ERC کیا ہے؟",
        description: "ایمرجنسی ریسکیو کوڈ (ERC) ایک منفرد، گاڑی کے لیے مخصوص کوڈ ہے جو مخصوص جاپانی Toyota گاڑیوں کے ہیڈ یونٹ کو ان لاک کرنے کے لیے استعمال ہوتا ہے۔ یہ کوڈ اس وقت ضروری ہے جب ہیڈ یونٹ لاک ہو جاتا ہے، جو بیٹری منقطع ہونے یا غلط انٹری کی کوشش جیسے واقعات کے بعد فعال ہونے والی سیکیورٹی خصوصیات کی وجہ سے ہو سکتا ہے۔"
      },
      howToFind: {
        title: "میں اپنا 16 ہندسوں کا کوڈ کیسے تلاش کروں؟",
        youtubeNote: "اپنے ماڈل کے لیے درست اقدامات جاننے کا بہترین طریقہ یہ ہے کہ اپنا ماڈل نمبر YouTube پر تلاش کریں۔ ہر یونٹ تھوڑا مختلف ہو سکتا ہے۔ نوٹ: بعض ماڈلز میں ترتیب کے حصے کے طور پر ہیڈلائٹس کو آن اور آف کرنا ضروری ہوتا ہے۔",
        description: "ERC کوڈ اسکرین تک رسائی کے دو عام طریقے ہیں۔ پہلے طریقہ 1 آزمائیں۔ اگر کام نہ کرے تو طریقہ 2 آزمائیں۔",
        steps: [
          "مین بٹن دبائیں اور پکڑیں: اپنے نیویگیشن پلیئر پر، مین بٹن کو دبائیں اور پکڑیں۔",
          "پارکنگ لائٹس آن/آف کریں: اپنی پارکنگ لائٹس کو 3 سے 4 بار آن اور آف کریں جب تک کہ ہیڈ یونٹ پر نئی اسکرین ظاہر نہ ہو۔",
          "ایکٹو بٹنز دبائیں: اسکرین پر موجود ہدایات کی پیروی کریں اور صرف ایکٹو بٹنز دبائیں۔ یہ آپ کو 16 ہندسوں کا ERC سیریل نمبر دکھانے والی اسکرین پر لے جائے گا۔",
          "اگر یہ اقدامات آپ کے مخصوص ماڈل کے لیے کام نہیں کرتے، تو آپ کو اپنی مخصوص گاڑی کے طریقہ کار کی تحقیق کرنے یا اضافی وسائل سے مشورہ کرنے کی ضرورت ہو سکتی ہے۔"
        ],
        methods: [
          {
            title: "طریقہ 1: MAP بٹن + چھوٹی لائٹ",
            intro: "نیویگیشن یونٹ پر موجود Current Location (MAP) بٹن کو دباتے ہوئے چھوٹی لائٹ کو 3 سے 4 بار آن/آف کر کے سروس انسپیکشن موڈ میں داخل ہوں۔",
            steps: [
              "نیویگیشن یونٹ کا Current Location (MAP) بٹن دبائیں اور پکڑیں۔",
              "بٹن دباتے ہوئے گاڑی کی چھوٹی لائٹ کو 3 سے 4 بار آن اور آف کریں۔",
              "اسکرین پر 'Service Inspection Mode' ظاہر ہونے پر بٹن چھوڑ دیں۔",
              "دکھائے گئے مینو سے 'Service Information' منتخب کریں۔",
              "'Security Code Entry' بٹن کو ٹچ کریں۔",
              "16 ہندسوں کا ERC کوڈ اسکرین پر ظاہر ہوگا۔ اسے نوٹ کریں۔"
            ]
          },
          {
            title: "طریقہ 2: پکچر کوالٹی ایڈجسٹمنٹ موڈ",
            intro: "پکچر کوالٹی ایڈجسٹمنٹ اسکرین سے مخصوص ٹچ آپریشنز کر کے سروس انسپیکشن موڈ تک رسائی حاصل کریں۔",
            steps: [
              "نیویگیشن یونٹ کا MENU بٹن لمبا دبائیں۔",
              "جب پکچر کوالٹی ایڈجسٹمنٹ موڈ ظاہر ہو تو اسکرین پر یہ پوزیشنز ترتیب سے ٹچ کریں: اوپر بائیں (A)، نیچے بائیں (B)، اوپر بائیں (A)، نیچے بائیں (B)، اوپر بائیں (A)، نیچے بائیں (B)۔",
              "درست ٹچ پر اسکرین سروس انسپیکشن موڈ میں بدل جائے گی۔",
              "دکھائے گئے مینو سے 'Service Information' منتخب کریں۔",
              "'Security Code Entry' بٹن کو ٹچ کریں۔",
              "16 ہندسوں کا ERC کوڈ اسکرین پر ظاہر ہوگا۔ اسے نوٹ کریں۔"
            ]
          }
        ]
      },
      supportedModels: {
        title: "سپورٹ شدہ ماڈلز",
        description: "ہمارا ERC ان لاک ٹول Toyota ہیڈ یونٹس کی ایک وسیع رینج کو سپورٹ کرتا ہے:"
      },
      credits: {
        freeUnlocksRemaining: "باقی مفت ان لاکس: {{count}}",
        creditsAvailable: "دستیاب کریڈٹس: {{count}}",
        buyCredits: "کریڈٹس خریدیں",
        needCredits: "ان لاک جاری رکھنے کے لیے آپ کو کریڈٹس کی ضرورت ہے"
      },
      payment: {
        title: "اپنا ان لاک پیکیج منتخب کریں",
        subtitle: "پہلے 2 ان لاکس مفت ہیں! بلک پیکجز کے ساتھ زیادہ بچائیں۔",
        credits: "کریڈٹس",
        perUnlock: "فی ان لاک",
        save: "بچائیں",
        popularBadge: "مقبول",
        purchaseButton: "ابھی خریدیں",
        processing: "پروسیسنگ...",
        securePayment: "🔒 Stripe کے ذریعے محفوظ ادائیگی",
        errorMessage: "ادائیگی ناکام۔ دوبارہ کوشش کریں۔"
      },
      whyLocked: {
        title: "میرا Toyota ہیڈ یونٹ کیوں لاک ہو جاتا ہے؟",
        intro: "اگر آپ کے پاس جاپان سے درآمد شدہ Toyota گاڑی ہے، تو آپ نے شاید اس مایوس کن صورتحال کا سامنا کیا ہو کہ ہیڈ یونٹ اچانک لاک ہو گیا اور کوڈ انٹری اسکرین ظاہر ہو گئی۔ یہ کوئی خرابی نہیں ہے، بلکہ یہ جاپانی گھریلو مارکیٹ (JDM) Toyota گاڑیوں میں بنایا گیا ایک جان بوجھ کر چوری مخالف سیکیورٹی فیچر ہے۔",
        sections: [
          {
            heading: "چوری مخالف نظام",
            text: "Toyota نے ERC لاک سسٹم کو خاص طور پر جاپانی گھریلو مارکیٹ کے لیے فروخت ہونے والی گاڑیوں کے لیے ڈیزائن کیا ہے۔ جب ہیڈ یونٹ کسی ممکنہ چوری کی صورتحال کا پتہ لگاتا ہے جیسے بیٹری کا اچانک بند ہونا تو یہ خود بخود لاک ہو جاتا ہے۔ اس سے چوروں کو یونٹ نکال کر دوسری گاڑی میں استعمال کرنے سے روکا جاتا ہے۔"
          },
          {
            heading: "لاک ہونے کی عام وجوہات",
            text: "ہیڈ یونٹ کئی عام صورتحالوں میں خود کو لاک کر لیتا ہے: بیٹری کا منقطع یا تبدیل ہونا، بیٹری کا مکمل ڈسچارج ہونا، گاڑی پر الیکٹریکل کام کرنا، یا متعدد غلط کوڈ انٹری کی کوششیں۔ درآمد شدہ گاڑیوں کے بہت سے مالکان اس مسئلے سے روزمرہ کی دیکھ بھال جیسے بیٹری تبدیل کرنے کے وقت گزرتے ہیں۔"
          },
          {
            heading: "درآمد شدہ گاڑیاں کیوں متاثر ہوتی ہیں",
            text: "جاپانی گھریلو مارکیٹ کی گاڑیاں اس توقع کے ساتھ جاپان میں فروخت کی گئی تھیں کہ مالکان ان لاک کوڈ حاصل کرنے کے لیے Toyota ڈیلرشپ جائیں گے۔ لیکن جب یہ گاڑیاں نیوزی لینڈ، آسٹریلیا، برطانیہ یا روس جیسے ممالک میں برآمد اور درآمد ہوتی ہیں تو اصل ڈیلرشپ سپورٹ دستیاب نہیں رہتی۔ یہی وجہ ہے کہ ہمارا ٹول موجود ہے۔"
          }
        ]
      },
      importGuide: {
        title: "جاپانی درآمد Toyota مالکان کے لیے گائیڈ",
        intro: "جاپانی گھریلو مارکیٹ Toyota گاڑیاں اپنی قابل اعتماد، کم میلیج اور اچھی طرح سے دیکھ بھال شدہ حالت کی وجہ سے بہت سے ممالک میں مقبول درآمدی گاڑیاں ہیں۔",
        sections: [
          {
            heading: "ہیڈ یونٹ کے فرق",
            text: "JDM Toyota ہیڈ یونٹ اکثر دوسری مارکیٹوں میں فروخت ہونے والوں سے مختلف ہوتے ہیں۔ ان میں جاپانی زبان کے مینو، جاپانی نقشوں کے ساتھ نیویگیشن سسٹم، اور جاپانی نشریاتی معیارات کے لیے ڈیجیٹل TV ٹیونر شامل ہو سکتے ہیں۔ اگر آپ اصل یونٹ رکھنا چاہتے ہیں تو ERC لاک سسٹم کو سمجھنا ضروری ہے۔"
          },
          {
            heading: "بیٹری کی دیکھ بھال کی تجاویز",
            text: "درآمد شدہ گاڑیوں میں ہیڈ یونٹ لاک ہونے کی سب سے عام وجوہات میں سے ایک باقاعدہ بیٹری تبدیلی ہے۔ کسی بھی وجہ سے بیٹری منقطع کرنے سے پہلے اس ٹول کو بُک مارک کریں یا ERC کوڈ نوٹ کریں۔ اس طرح اگر بیٹری دوبارہ جوڑنے کے بعد ہیڈ یونٹ لاک ہو جائے تو آپ اسے سیکنڈوں میں ان لاک کر سکتے ہیں۔"
          },
          {
            heading: "اپنا ERC کوڈ محفوظ رکھیں",
            text: "ایک بار جب آپ اپنے ہیڈ یونٹ سے 16 ہندسوں کا ERC سیریل نمبر کامیابی سے حاصل کر لیں تو اسے کسی محفوظ جگہ محفوظ کریں۔ گاڑی کی سروس بک میں نوٹ کریں، فون میں سیو کریں، یا کسی محفوظ نوٹس ایپ میں اسٹور کریں۔ آپ کو مستقبل میں دوبارہ اس کی ضرورت پڑ سکتی ہے۔"
          },
          {
            heading: "پیشہ ور مدد کب لیں",
            text: "جبکہ ہمارا ERC ان لاک ٹول بیشتر جاپانی Toyota ہیڈ یونٹس کو سنبھالتا ہے، کچھ پرانے یا بالکل نئے ماڈل مختلف لاکنگ سسٹم استعمال کر سکتے ہیں۔ اگر آپ نے تیار کردہ کوڈ داخل کیا اور یہ کئی کوششوں کے بعد بھی کام نہیں کرتا تو کسی ماہر آٹو الیکٹریشن سے مشورہ لینا مفید ہوگا۔"
          }
        ]
      },
      faq: {
        title: "اکثر پوچھے گئے سوالات",
        items: [
          {
            question: "کیا یہ ٹول مکمل طور پر مفت ہے؟",
            answer: "جی ہاں۔ ہر نئے صارف کو بغیر سائن اپ یا ادائیگی کے 2 مفت ان لاکس ملتے ہیں۔ اگر آپ کو مزید ان لاکس کی ضرورت ہے تو سستے کریڈٹ پیک دستیاب ہیں۔"
          },
          {
            question: "میرا Toyota ہیڈ یونٹ کیوں لاک ہو جاتا ہے؟",
            answer: "ہیڈ یونٹ خود بخود چوری مخالف اقدام کے طور پر لاک ہو جاتا ہے۔ یہ اکثر بیٹری منقطع یا تبدیل ہونے کے بعد، بیٹری مکمل ڈسچارج ہونے کے بعد، یا متعدد غلط کوڈ انٹری کی کوششوں کے بعد ہوتا ہے۔"
          },
          {
            question: "کیا یہ میرے مخصوص ماڈل پر کام کرے گا؟",
            answer: "ہمارا ٹول جاپانی Toyota ہیڈ یونٹس کی ایک وسیع رینج کو سپورٹ کرتا ہے۔ اس صفحے پر سپورٹ شدہ ماڈلز کی فہرست دیکھیں۔"
          },
          {
            question: "کیا اپنا ہیڈ یونٹ ان لاک کرنا قانونی ہے؟",
            answer: "جی ہاں، بالکل۔ آپ اپنی گاڑی کا ہیڈ یونٹ ان لاک کر رہے ہیں۔ یہ ٹول ان گاڑیوں کے مالکان اور پیشہ ور افراد کے لیے ہے جنہیں بیٹری تبدیل ہونے جیسے معمول کے حالات میں لاک ہونے والے ہیڈ یونٹ تک رسائی بحال کرنی ہو۔"
          },
          {
            question: "اگر کوڈ کام نہ کرے تو کیا کریں؟",
            answer: "پہلے یہ چیک کریں کہ آپ نے 16 ہندسوں کا ERC سیریل نمبر صحیح داخل کیا ہے۔ ایک بھی غلط حرف غلط ان لاک کوڈ پیدا کرے گا۔ حرف O اور ہندسہ 0، حرف I اور ہندسہ 1 کو یقینی بنائیں کہ آپ نے غلط نہ لکھا ہو۔"
          },
          {
            question: "کیا تکنیکی علم ضروری ہے؟",
            answer: "کوئی تکنیکی علم ضروری نہیں۔ بس اپنے لاک ہیڈ یونٹ پر دکھائے گئے 16 ہندسوں کا کوڈ حاصل کریں، اسے اس صفحے کے ٹول میں داخل کریں، اور آپ کا ان لاک کوڈ فوری طور پر تیار ہو جائے گا۔"
          },
          {
            question: "ان لاک کے عمل میں کتنا وقت لگتا ہے؟",
            answer: "16 ہندسوں کا سیریل نمبر داخل کرنے کے بعد ان لاک کوڈ تیار کرنے میں ایک سیکنڈ سے بھی کم وقت لگتا ہے۔ ہیڈ یونٹ میں کوڈ داخل کرنے اور ان لاک مکمل کرنے میں عام طور پر 1 سے 2 منٹ لگتے ہیں۔"
          },
          {
            question: "کیا میں متعدد گاڑیوں کے لیے یہ ٹول استعمال کر سکتا ہوں؟",
            answer: "جی ہاں۔ ہر گاڑی کا منفرد 16 ہندسوں کا ERC سیریل نمبر ہوتا ہے۔ اگر آپ متعدد درآمد شدہ Toyota گاڑیوں کے ساتھ کام کرتے ہیں تو ہمارے کریڈٹ پیک میکینکس اور ڈیلرز کے لیے ڈیزائن کیے گئے ہیں۔"
          }
        ]
      },
      troubleshooting: {
        title: "عام مسائل کا حل",
        intro: "اگر آپ کو اپنے Toyota ہیڈ یونٹ کو ان لاک کرنے میں پریشانی ہو رہی ہے تو یہ گائیڈ سب سے عام مسائل اور ان کے حل کا احاطہ کرتی ہے۔",
        items: [
          {
            problem: "میں ہیڈ یونٹ کو 16 ہندسوں کا کوڈ دکھانے پر مجبور نہیں کر سکتا",
            solution: "ERC سیریل نمبر حاصل کرنے کا صحیح طریقہ ماڈل اور سال کے لحاظ سے مختلف ہوتا ہے۔ سب سے عام طریقہ ہے کہ مین پاور بٹن کو دبائے رکھتے ہوئے پارکنگ لائٹس آن آف کریں۔ اپنے مخصوص ماڈل نمبر کے ساتھ ERC code retrieval تلاش کریں۔"
          },
          {
            problem: "میرا تیار کردہ ان لاک کوڈ کام نہیں کرتا",
            solution: "سب سے عام وجہ 16 ہندسوں کے سیریل نمبر میں ٹائپنگ کی غلطی ہے۔ ERC کوڈز میں نمبر اور حروف (A-F) دونوں استعمال ہوتے ہیں۔ یقینی بنائیں کہ آپ نے حرف O اور نمبر 0، حرف I اور نمبر 1 کو آپس میں نہ ملایا ہو۔"
          },
          {
            problem: "ان لاک کرنے کے بعد ہیڈ یونٹ دوبارہ لاک ہو گیا",
            solution: "جب بھی ہیڈ یونٹ بجلی میں رکاوٹ محسوس کرتا ہے تو دوبارہ لاک ہو جاتا ہے۔ یہ معمول کا رویہ ہے۔ اگر آپ کی بیٹری پرانی ہے اور بار بار ڈاؤن ہو رہی ہے تو بیٹری تبدیل کرنے پر غور کریں۔"
          },
          {
            problem: "ہیڈ یونٹ کی سکرین خالی ہے یا بالکل جواب نہیں دے رہی",
            solution: "اگر سکرین کوڈ انٹری پرامپٹ کی بجائے خالی ہے تو یہ کسی اور مسئلے کی نشاندہی کر سکتا ہے جیسے فیوز اڑ جانا، ڈھیلا وائرنگ کنیکٹر، یا خراب سکرین۔ آڈیو یا نیویگیشن سسٹم سے متعلق فیوز بکس چیک کریں۔"
          },
          {
            problem: "میں نے اپنے تمام مفت ان لاکس استعمال کر لیے ہیں",
            solution: "مفت ٹیئر نئے صارفین کو 2 ان لاکس فراہم کرتا ہے۔ اگر آپ کو اضافی ان لاکس کی ضرورت ہے تو سستے کریڈٹ پیک خریدے جا سکتے ہیں۔"
          }
        ]
      },
      nav: {
        home: "ہوم",
        whatIsErc: "ERC کیا ہے؟",
        howToFind: "کوڈ کیسے تلاش کریں",
        supportedModels: "سپورٹ شدہ ماڈلز",
        faq: "اکثر سوالات",
        troubleshooting: "مسائل کا حل"
      },
      contact: {
        title: "رابطہ کریں",
        subtitle: "ٹول یا ان لاک کوڈ کے بارے میں کوئی سوال ہے؟ ہم مدد کے لیے یہاں ہیں۔",
        nameLabel: "آپ کا نام",
        namePlaceholder: "مثال: احمد علی",
        emailLabel: "ای میل پتہ",
        emailPlaceholder: "مثال: ahmad@example.com",
        subjectLabel: "موضوع",
        subjectPlaceholder: "مثال: میرا ان لاک کوڈ کام نہیں کر رہا",
        messageLabel: "پیغام",
        messagePlaceholder: "ہمیں بتائیں آپ کو کس چیز میں مدد چاہیے...",
        submitButton: "پیغام بھیجیں",
        submitting: "بھیج رہے ہیں...",
        successTitle: "پیغام بھیج دیا گیا!",
        successMessage: "رابطہ کرنے کا شکریہ۔ ہم جلد از جلد جواب دیں گے۔",
        errorMessage: "کچھ غلط ہو گیا۔ دوبارہ کوشش کریں۔"
      },
      steps: {
        step1: {
          title: "آپ کا ہیڈ یونٹ لاک ہے",
          description: "بیٹری تبدیل کرنے کے بعد ہیڈ یونٹ اس طرح کی اسکرین دکھا سکتا ہے۔ آپ کو ERC سیریل نمبر اسکرین تک پہنچ کر اپنا 16 ہندسوں کا کوڈ حاصل کرنا ہوگا۔"
        },
        step2: {
          title: "اپنے اسٹیریو کا ماڈل نمبر تلاش کریں",
          description: "ماڈل نمبر ہیڈ یونٹ پر لگے اسٹیکر پر درج ہوتا ہے۔ ماڈل نمبر معلوم ہونے کے بعد YouTube پر سرچ کریں تاکہ اپنے مخصوص ماڈل کی ERC سیریل اسکرین تک پہنچنے کا طریقہ معلوم کر سکیں۔",
          note: "💡 نوٹ: کچھ ماڈلز میں ترتیب کے حصے کے طور پر ہیڈلائٹس کو آن اور آف کرنا ضروری ہوتا ہے۔"
        },
        step3: {
          title: "16 ہندسوں کا کوڈ اوپر دیے گئے ٹول میں درج کریں",
          description: "اس اسکرین پر دکھائے گئے 16 ہندسوں کا کوڈ اس صفحے کے اوپری حصے میں موجود ٹول میں ٹائپ کریں۔ آپ کو فوری طور پر ان لاک کوڈ مل جائے گا۔ وہ ان لاک کوڈ اپنے اسٹیریو میں درج کریں تاکہ ہیڈ یونٹ دوبارہ کام کرنے لگے۔"
        }
      }
    },
    ru: {
      title: "Бесплатный инструмент разблокировки Toyota ERC Головного устройства",
      explanation: "Наш инструмент предоставляет быстрый и простой способ генерации кодов разблокировки ERC бесплатно, помогая вам восстановить полную функциональность вашего японского головного устройства Toyota без необходимости дорогостоящих визитов к дилеру.",
      navigationButtons: {
        whatIsErc: "Что такое ERC?",
        howToFind: "Как найти мой 16-значный код?"
      },
      inputLabel: "ERC код (16 символов):",
      inputPlaceholder: "например: 9F8E7D6C5B4A3210",
      decodeButton: "Декодировать",
      generateAnotherButton: "Сгенерировать другой код",
      errorMessage: "Неверное количество символов.",
      whatIsErc: {
        title: "Что такое ERC?",
        description: "Аварийный спасательный код (ERC), это уникальный, специфичный для транспортного средства код, используемый для разблокировки головного устройства определенных японских автомобилей Toyota. Этот код необходим, когда головное устройство заблокировано, что может произойти из-за функций безопасности, активированных после таких событий, как отключение аккумулятора или неправильные попытки ввода."
      },
      howToFind: {
        title: "Как найти мой 16-значный код?",
        youtubeNote: "Лучший способ найти точные шаги для вашей модели — найти номер модели на YouTube. Каждое устройство может немного отличаться. Обратите внимание: на некоторых моделях часть последовательности включает включение и выключение фар.",
        description: "Существует два распространённых способа доступа к экрану с ERC-кодом. Сначала попробуйте Способ 1. Если он не сработает, попробуйте Способ 2.",
        steps: [
          "Нажмите и удерживайте главную кнопку: На вашем навигационном плеере нажмите и удерживайте главную кнопку.",
          "Включите/выключите габаритные огни: Переключайте габаритные огни 3-4 раза до тех пор, пока на головном устройстве не появится новый экран.",
          "Нажмите активные кнопки: Следуйте подсказкам на экране и нажимайте только активные кнопки. Это приведет вас к экрану с 16-значным серийным номером ERC.",
          "Если эти шаги не работают для вашей модели, изучите процедуру для вашего конкретного автомобиля или обратитесь к дополнительным ресурсам."
        ],
        methods: [
          {
            title: "Способ 1: Кнопка MAP + Габаритный свет",
            intro: "Войдите в режим сервисной инспекции, удерживая кнопку «Текущее местоположение (MAP)» на навигационном блоке и одновременно включая и выключая габаритный свет 3-4 раза.",
            steps: [
              "Нажмите и удерживайте кнопку «Текущее местоположение (MAP)» на навигационном блоке.",
              "Удерживая кнопку, включите и выключите габаритный свет автомобиля 3-4 раза.",
              "Отпустите кнопку, когда на экране появится «Режим сервисной инспекции».",
              "Выберите «Сервисная информация» из отображаемого меню.",
              "Нажмите кнопку «Ввод кода безопасности».",
              "На экране отобразится 16-значный ERC-код. Запишите его."
            ]
          },
          {
            title: "Способ 2: Режим регулировки качества изображения",
            intro: "Получите доступ к режиму сервисной инспекции, выполнив определённые касания с экрана регулировки качества изображения.",
            steps: [
              "Долго нажмите кнопку MENU на навигационном блоке.",
              "Когда появится режим регулировки качества изображения, последовательно коснитесь следующих позиций на экране: верхний левый (A), нижний левый (B), верхний левый (A), нижний левый (B), верхний левый (A), нижний левый (B).",
              "При правильных касаниях экран переключится в режим сервисной инспекции.",
              "Выберите «Сервисная информация» из отображаемого меню.",
              "Нажмите кнопку «Ввод кода безопасности».",
              "На экране отобразится 16-значный ERC-код. Запишите его."
            ]
          }
        ]
      },
      supportedModels: {
        title: "Поддерживаемые модели",
        description: "Наш инструмент разблокировки ERC поддерживает широкий спектр головных устройств Toyota:"
      },
      credits: {
        freeUnlocksRemaining: "Оставшиеся бесплатные разблокировки: {{count}}",
        creditsAvailable: "Доступные кредиты: {{count}}",
        buyCredits: "Купить кредиты",
        needCredits: "Вам нужны кредиты, чтобы продолжить разблокировку"
      },
      payment: {
        title: "Выберите пакет разблокировки",
        subtitle: "Первые 2 разблокировки БЕСПЛАТНО! Экономьте больше с оптовыми пакетами.",
        credits: "кредитов",
        perUnlock: "за разблокировку",
        save: "Экономия",
        popularBadge: "ПОПУЛЯРНЫЙ",
        purchaseButton: "Купить сейчас",
        processing: "Обработка...",
        securePayment: "🔒 Безопасная оплата через Stripe",
        errorMessage: "Платеж не прошел. Пожалуйста, попробуйте снова."
      },
      whyLocked: {
        title: "Почему блокируется головное устройство Toyota?",
        intro: "Если у вас есть японский импортный автомобиль Toyota, вы, возможно, сталкивались с неприятной ситуацией, когда головное устройство внезапно блокируется и отображает экран ввода кода. Это не неисправность, это намеренная защита от кражи, встроенная во многие автомобили японского внутреннего рынка (JDM).",
        sections: [
          {
            heading: "Система защиты от кражи",
            text: "Toyota разработала систему блокировки ERC специально для автомобилей, продаваемых на японском внутреннем рынке. Когда головное устройство обнаруживает потенциальную ситуацию кражи, например, внезапное отключение питания из-за отсоединения аккумулятора, оно автоматически блокируется. Это не позволяет ворам снять устройство и использовать его в другом автомобиле, поскольку без правильного кода разблокировки оно становится бесполезным."
          },
          {
            heading: "Распространённые причины блокировки",
            text: "Головное устройство блокируется в нескольких распространённых ситуациях: отсоединение или замена аккумулятора, полный разряд аккумулятора, электрические работы на автомобиле, или несколько неправильных попыток ввода кода. Многие владельцы импортных автомобилей сталкиваются с этим при обычном техническом обслуживании, например при замене аккумулятора."
          },
          {
            heading: "Почему страдают импортные автомобили",
            text: "Автомобили японского внутреннего рынка продавались в Японии в расчёте на то, что владельцы обратятся в дилерский центр Toyota для получения кода разблокировки. Однако когда эти автомобили экспортируются в такие страны, как Новая Зеландия, Австралия, Великобритания или Россия, поддержка оригинального дилера становится недоступной. Именно поэтому существует наш инструмент."
          }
        ]
      },
      importGuide: {
        title: "Руководство для владельцев импортных Toyota из Японии",
        intro: "Автомобили Toyota японского внутреннего рынка, популярные импортные автомобили во многих странах, ценимые за надёжность, небольшой пробег и хорошее техническое состояние.",
        sections: [
          {
            heading: "Отличия головных устройств",
            text: "Головные устройства JDM Toyota часто отличаются от тех, что продаются на других рынках. Они могут отображать меню на японском языке, иметь навигационные системы с японскими картами и включать цифровые ТВ-тюнеры для японских стандартов вещания. Если вы хотите сохранить оригинальное устройство, важно понимать, как управлять системой блокировки ERC."
          },
          {
            heading: "Советы по обслуживанию аккумулятора",
            text: "Одна из наиболее распространённых причин блокировки головных устройств на импортных автомобилях, плановая замена аккумулятора. Прежде чем отсоединять аккумулятор по любой причине, добавьте этот инструмент в закладки или запишите ваш ERC-код. Тогда, если головное устройство заблокируется после повторного подключения аккумулятора, вы сможете разблокировать его за секунды."
          },
          {
            heading: "Сохраняйте ваш ERC-код в безопасном месте",
            text: "Как только вы успешно получите 16-значный серийный номер ERC из вашего головного устройства, сохраните его в надёжном месте. Сделайте заметку в сервисной книжке автомобиля, сохраните в телефоне или в защищённом приложении для заметок. В будущем он может понадобиться снова."
          },
          {
            heading: "Когда обратиться к специалисту",
            text: "Хотя наш инструмент разблокировки ERC охватывает подавляющее большинство японских головных устройств Toyota, некоторые старые или очень новые модели могут использовать другую систему блокировки. Если введённый код не работает после нескольких попыток, стоит обратиться к специалисту по автоэлектрике или импортёру, специализирующемуся на JDM-автомобилях."
          }
        ]
      },
      faq: {
        title: "Часто задаваемые вопросы",
        items: [
          {
            question: "Инструмент полностью бесплатен?",
            answer: "Да. Каждый новый пользователь получает 2 бесплатные разблокировки без регистрации и оплаты. Если вам нужно больше разблокировок, например, если вы механик или автодилер, работающий с импортными Toyota, доступны доступные по цене пакеты кредитов."
          },
          {
            question: "Почему блокируется головное устройство Toyota?",
            answer: "Головное устройство автоматически блокируется как мера защиты от кражи. Чаще всего это происходит после отсоединения или замены аккумулятора, полного разряда аккумулятора или нескольких неправильных попыток ввода кода."
          },
          {
            question: "Подойдёт ли инструмент для моей модели?",
            answer: "Наш инструмент поддерживает широкий спектр японских головных устройств Toyota. Проверьте список поддерживаемых моделей на этой странице."
          },
          {
            question: "Законно ли разблокировать собственное головное устройство?",
            answer: "Да, абсолютно законно. Вы разблокируете головное устройство собственного автомобиля. Этот инструмент предназначен для владельцев автомобилей и автомобильных специалистов, которым нужно восстановить доступ к заблокированному устройству."
          },
          {
            question: "Что делать, если код не работает?",
            answer: "Сначала убедитесь, что вы правильно ввели 16-значный серийный номер ERC. Один неверный символ даст неправильный код разблокировки. Убедитесь, что не перепутали букву O с цифрой 0 или букву I с цифрой 1."
          },
          {
            question: "Нужны ли технические знания?",
            answer: "Технических знаний не требуется. Просто получите 16-значный код, отображаемый на заблокированном головном устройстве, введите его в инструмент на этой странице, и код разблокировки будет мгновенно сгенерирован."
          },
          {
            question: "Сколько времени занимает процесс разблокировки?",
            answer: "Генерация кода разблокировки занимает менее секунды после ввода 16-значного серийного номера. Ввод кода в головное устройство и завершение процесса разблокировки обычно занимает 1-2 минуты."
          },
          {
            question: "Можно ли использовать инструмент для нескольких автомобилей?",
            answer: "Да. У каждого автомобиля уникальный 16-значный серийный номер ERC, поэтому сгенерированный код разблокировки разный для каждого автомобиля. Если вы работаете с несколькими импортными Toyota, наши пакеты кредитов разработаны с учётом потребностей механиков и дилеров."
          }
        ]
      },
      troubleshooting: {
        title: "Устранение распространённых проблем",
        intro: "Если у вас возникли трудности с разблокировкой головного устройства Toyota, следующее руководство охватывает наиболее распространённые проблемы и способы их решения.",
        items: [
          {
            problem: "Не могу заставить головное устройство отобразить 16-значный код",
            solution: "Точный метод получения серийного номера ERC варьируется в зависимости от модели и года выпуска. Наиболее распространённый способ, нажать и удерживать главную кнопку питания, переключая габаритные огни. Найдите номер вашей конкретной модели вместе с запросом 'ERC code retrieval' для получения инструкций."
          },
          {
            problem: "Сгенерированный код разблокировки не работает",
            solution: "Наиболее распространённая причина, опечатка в 16-значном серийном номере. Коды ERC используют как цифры, так и буквы (A-F), поэтому убедитесь, что не перепутали букву O с цифрой 0 или букву I с цифрой 1. Внимательно введите серийный номер повторно и сгенерируйте код снова."
          },
          {
            problem: "После разблокировки головное устройство снова заблокировалось",
            solution: "Головное устройство повторно блокируется при каждом обнаружении перебоя питания. Это нормальное поведение. Если аккумулятор старый и регулярно разряжается, это может вызывать периодические блокировки. Рассмотрите замену аккумулятора."
          },
          {
            problem: "Экран головного устройства пустой или полностью не реагирует",
            solution: "Если экран пустой, а не показывает запрос ввода кода, это может указывать на другую проблему, перегоревший предохранитель, незакреплённый разъём или неисправный экран. Проверьте блок предохранителей на наличие перегоревших предохранителей, связанных с аудио или навигацией."
          },
          {
            problem: "Я использовал все бесплатные разблокировки",
            solution: "Бесплатный уровень предоставляет новым пользователям 2 разблокировки. Если вам нужны дополнительные разблокировки, вы можете приобрести пакеты кредитов по доступной цене, идеально подходящие для механиков и автодилеров."
          }
        ]
      },
      nav: {
        home: "Главная",
        whatIsErc: "Что такое ERC?",
        howToFind: "Как найти код",
        supportedModels: "Поддерживаемые модели",
        faq: "Часто задаваемые вопросы",
        troubleshooting: "Устранение неполадок"
      },
      contact: {
        title: "Связаться с нами",
        subtitle: "Есть вопросы об инструменте или коде разблокировки? Мы здесь, чтобы помочь.",
        nameLabel: "Ваше имя",
        namePlaceholder: "например: Иван Иванов",
        emailLabel: "Адрес электронной почты",
        emailPlaceholder: "например: ivan@example.com",
        subjectLabel: "Тема",
        subjectPlaceholder: "например: Код разблокировки не работает",
        messageLabel: "Сообщение",
        messagePlaceholder: "Расскажите, с чем вам нужна помощь...",
        submitButton: "Отправить сообщение",
        submitting: "Отправка...",
        successTitle: "Сообщение отправлено!",
        successMessage: "Спасибо за обращение. Мы ответим вам как можно скорее.",
        errorMessage: "Что-то пошло не так. Пожалуйста, попробуйте ещё раз."
      },
      steps: {
        step1: {
          title: "Головное устройство заблокировано",
          description: "После замены аккумулятора или других работ головное устройство может показать такой экран. Вам нужно перейти к экрану с ERC серийным номером, чтобы получить 16-значный код."
        },
        step2: {
          title: "Найдите номер модели вашей магнитолы",
          description: "Номер модели указан на наклейке на самом головном устройстве. Найдя его, введите в поиск на YouTube, чтобы узнать точную последовательность кнопок для перехода к экрану ERC серийного номера на вашей конкретной модели.",
          note: "💡 Примечание: некоторые модели требуют включения и выключения фар в качестве части последовательности."
        },
        step3: {
          title: "Введите 16-значный код в инструмент выше",
          description: "Введите 16-значный код, показанный на этом экране, в инструмент в верхней части страницы. Вы мгновенно получите код разблокировки. Введите этот код разблокировки в магнитолу, чтобы восстановить её работу."
        }
      }
    },
    paymentSuccess: {
      en: {
        title: "Payment Successful!",
        message: "Your credits have been added to your account.",
        redirecting: "Redirecting you back to the unlock tool..."
      },
      ja: {
        title: "支払い成功！",
        message: "クレジットがアカウントに追加されました。",
        redirecting: "ロック解除ツールにリダイレクトしています..."
      },
      mn: {
        title: "Төлбөр амжилттай!",
        message: "Таны кредитүүд таны дансанд нэмэгдлээ.",
        redirecting: "Таныг түгжээ тайлах хэрэгсэл рүү буцааж байна..."
      },
      ur: {
        title: "ادائیگی کامیاب!",
        message: "آپ کے کریڈٹس آپ کے اکاؤنٹ میں شامل کر دیے گئے ہیں۔",
        redirecting: "آپ کو واپس ان لاک ٹول پر بھیج رہے ہیں..."
      },
      ru: {
        title: "Платеж успешен!",
        message: "Кредиты добавлены в вашу учетную запись.",
        redirecting: "Перенаправляем вас обратно к инструменту разблокировки..."
      }
    }
  };
  
  export default translations;
